import { useCallback, useEffect, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, RefObject } from 'react';

type ResizeState = {
    colIndex: number;
    startX: number;
    startWidth: number;
} | null;

type UseColumnResizeProps = {
    disabled: boolean;
    columnWidthsRef: RefObject<number[]>;
    onUpdateWidths: (widths: number[]) => void;
};

type UseColumnResizeResult = {
    handleResizeStart: (colIndex: number, event: ReactMouseEvent) => void;
};

export const useColumnResize = ({
    disabled,
    columnWidthsRef,
    onUpdateWidths,
}: UseColumnResizeProps): UseColumnResizeResult => {
    const [resizeState, setResizeState] = useState<ResizeState>(null);

    const handleResizeStart = useCallback(
        (colIndex: number, event: ReactMouseEvent) => {
            if (disabled) return;
            setResizeState({
                colIndex,
                startX: event.clientX,
                startWidth: columnWidthsRef.current?.[colIndex] || 150,
            });
        },
        [columnWidthsRef, disabled],
    );

    useEffect(() => {
        if (!resizeState) return;

        const handleMouseMove = (event: MouseEvent) => {
            const delta = event.clientX - resizeState.startX;
            const nextWidth = Math.max(60, resizeState.startWidth + delta);
            const nextWidths = [...(columnWidthsRef.current || [])];
            nextWidths[resizeState.colIndex] = nextWidth;
            onUpdateWidths(nextWidths);
        };

        const handleMouseUp = () => {
            setResizeState(null);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [columnWidthsRef, onUpdateWidths, resizeState]);

    return { handleResizeStart };
};
